import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { get, isNull } from "lodash";
import ServiceProvider from "@client.services/provider";
import { getError } from "@client.utils/errors";

const DEFAULT_DATA = { data: [], load: false };

const initialState = {
  users: DEFAULT_DATA,
  salaryRanges: [],
  teams: [],
  error: null,
  userManagementError: null,
  availableRoles: [],
  notificationSettings: DEFAULT_DATA,
};

export const createUserAsync = createAsyncThunk(
  "settings/createUser",
  async (args, thunkAPI) => {
    const data = await ServiceProvider.User.create(args);
    thunkAPI.dispatch(clearUserManagementError());
    await thunkAPI.dispatch(getUsersAsync());
    return data;
  }
);

export const updateUserAsync = createAsyncThunk(
  "settings/updateUser",
  async (args, thunkAPI) => {
    const data = await ServiceProvider.User.update(args);
    thunkAPI.dispatch(clearUserManagementError());
    await thunkAPI.dispatch(getUsersAsync());
    return data;
  }
);

export const deleteUserAsync = createAsyncThunk(
  "settings/deleteUser",
  async (args, thunkAPI) => {
    const data = await ServiceProvider.User.delete(args);
    await thunkAPI.dispatch(getUsersAsync());
    return data;
  }
);

export const getUsersAsync = createAsyncThunk("settings/getUsers", async () => {
  return await ServiceProvider.User.getAll();
});

export const getSalaryRangesAsync = createAsyncThunk(
  "settings/salaryRanges",
  async () => {
    return await ServiceProvider.SalaryRange.getAll();
  }
);

export const getTeamsAsync = createAsyncThunk("settings/teams", async () => {
  return await ServiceProvider.Team.getAll();
});

export const getAvailableRolesAsync = createAsyncThunk(
  "settings/getAvailableRoles",
  async () => {
    return await ServiceProvider.User.getAvailableRoles();
  }
);

export const getNotificationSettingsAsync = createAsyncThunk(
  "settings/notificationSettings/getAll",
  async (isSilent, thunkAPI) => {
    try {
      if (!isSilent) {
        thunkAPI.dispatch(setNotificationSettingsLoading(true));
      }
      return await ServiceProvider.UserNotificationSetting.getAll();
    } finally {
      if (!isSilent) {
        thunkAPI.dispatch(setNotificationSettingsLoading(false));
      }
    }
  }
);

export const updateNotificationSettingAsync = createAsyncThunk(
  "settings/notificationSettings/update",
  async (setting) => {
    return await ServiceProvider.UserNotificationSetting.update(setting);
  }
);

export const settingsSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {
    setSettingsError: (state, action) => {
      state.error = get(action, "payload", null);
    },
    clearUserManagementError: (state) => {
      state.userManagementError = null;
    },
    setNotificationSettingsLoading: (state, action) => {
      state.notificationSettings.load = get(action, "payload", null);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createUserAsync.rejected, (state, action) => {
        const msg = getError(action);
        state.userManagementError = isNull(msg) ? null : JSON.parse(msg)[0].msg;
      })
      .addCase(updateUserAsync.rejected, (state, action) => {
        state.error = getError(action);
      })
      .addCase(deleteUserAsync.rejected, (state, action) => {
        state.error = getError(action);
      })
      .addCase(getAvailableRolesAsync.fulfilled, (state, action) => {
        state.availableRoles = get(action, "payload", []);
      })
      .addCase(getAvailableRolesAsync.rejected, (state, action) => {
        state.availableRoles = [];
        state.error = getError(action);
      })
      .addCase(getSalaryRangesAsync.fulfilled, (state, action) => {
        state.salaryRanges = get(action, "payload", []);
      })
      .addCase(getSalaryRangesAsync.rejected, (state, action) => {
        state.salaryRanges = [];
        state.error = getError(action);
      })
      .addCase(getTeamsAsync.fulfilled, (state, action) => {
        state.teams = get(action, "payload", []);
      })
      .addCase(getTeamsAsync.rejected, (state, action) => {
        state.teams = [];
        state.error = getError(action);
      })
      .addCase(getUsersAsync.pending, (state) => {
        state.users.load = true;
      })
      .addCase(getUsersAsync.fulfilled, (state, action) => {
        state.users = {
          data: get(action, "payload", []),
          load: false,
        };
      })
      .addCase(getUsersAsync.rejected, (state, action) => {
        state.users = { ...DEFAULT_DATA };
        state.error = getError(action);
      })
      .addCase(getNotificationSettingsAsync.fulfilled, (state, action) => {
        state.notificationSettings.data = get(action, "payload", []);
      })
      .addCase(getNotificationSettingsAsync.rejected, (state, action) => {
        state.notificationSettings = { ...DEFAULT_DATA };
        state.error = getError(action);
      })
      .addCase(updateNotificationSettingAsync.fulfilled, (state, action) => {
        const updated = action.meta.arg;
        const index = state.notificationSettings.data.findIndex(
          (item) => item.id === updated.id
        );

        if (index !== -1) {
          state.notificationSettings.data[index] = updated;
        }
      });
  },
});

export const {
  setNotificationSettingsLoading,
  setSettingsError,
  clearUserManagementError,
} = settingsSlice.actions;

export const makeUsers = (state) => state.settings.users;
export const makeSalaryRanges = (state) => state.settings.salaryRanges;
export const makeTeams = (state) => state.settings.teams;
export const makeAvailableRoles = (state) => state.settings.availableRoles;
export const makeNotificationSettings = (state) =>
  state.settings.notificationSettings;
export const makeSettingsError = (state) => state.settings.error;
export const makeUserManagementError = (state) =>
  state.settings.userManagementError;

export default settingsSlice.reducer;
